'use client';

import React from 'react';

import { resolveAuthTokenSync } from '@/services/httpServices/authTokenResolver';
import type { HttpServiceConfig } from '@/services/httpServices/types';
import { useIsSpaceHydrated, useSpaceId } from '@/stores/space';
import { auth } from '@/utils/firebase-helpers';
import { getTargetEnvironmentClient } from '@/utils/target-detection';

// Shared module-level state so every `useQueryConfig` consumer subscribes to a
// single Firebase token listener (not one per hook call). The first call mounts
// the listener; later calls reuse it and React's `useSyncExternalStore` keeps
// every subscriber in sync. We flip `tokenReady` to `true` as soon as Firebase
// reports the first non-null user (token has been written to the JWT cookie by
// the global auth provider), which unblocks queries that would otherwise fire
// before the cookie is available and 401 with "Something went wrong".
let tokenReady = false;
const tokenReadyListeners = new Set<() => void>();
let firebaseSubscribed = false;

function subscribeToTokenReady(notify: () => void): () => void {
  tokenReadyListeners.add(notify);

  if (firebaseSubscribed === false && typeof window !== 'undefined') {
    firebaseSubscribed = true;
    // Seed from the cookie — if the JWT is already there (warm navigation),
    // queries can fire immediately.
    if (resolveAuthTokenSync() !== '') {
      tokenReady = true;
    }
    auth()
      .then((authInstance) => {
        // onIdTokenChanged fires on initial state resolution AND on every
        // refresh, so it captures the post-bootstrap moment when the JWT
        // cookie has just been written.
        authInstance.onIdTokenChanged((firebaseUser) => {
          const nextReady = firebaseUser !== null;
          if (nextReady !== tokenReady) {
            tokenReady = nextReady;
            tokenReadyListeners.forEach(listener => listener());
          }
        });
      })
      .catch(() => {
        // Auth bootstrap failed — leave tokenReady as-is; the cookie path
        // (if any) still works.
      });
  }

  return () => {
    tokenReadyListeners.delete(notify);
  };
}

function getTokenReadySnapshot(): boolean {
  return tokenReady;
}

function getTokenReadyServerSnapshot(): boolean {
  // Queries are disabled on the server anyway (space store not hydrated), so
  // the snapshot value here doesn't reach the network. Keep it false so SSR
  // and first-paint CSR agree.
  return false;
}

function useTokenReady(): boolean {
  return React.useSyncExternalStore(
    subscribeToTokenReady,
    getTokenReadySnapshot,
    getTokenReadyServerSnapshot,
  );
}

/**
 * Query configuration result with space dependency enforcement
 */
export type QueryConfigResult = {
  /** HTTP request config with current space from store */
  httpRequestConfig: HttpServiceConfig;
  /** Whether queries should be enabled (space must exist and be hydrated) */
  isEnabled: boolean;
  /** Current space ID */
  spaceId: string;
  /** Whether space is available */
  hasSpace: boolean;
  /** Whether space store is hydrated */
  isHydrated: boolean;
};

/**
 * Centralized hook for query configuration
 *
 * Enforces space as a blocking dependency for all queries:
 * - Uses space store as single source of truth (synced from URL path)
 * - Returns isEnabled=false when no space is set or store not hydrated
 * - Provides consistent httpRequestConfig across all queries
 * - Automatically re-renders when space changes
 *
 * Usage:
 * ```tsx
 * const { httpRequestConfig, isEnabled } = useQueryConfig();
 *
 * const query = useQuery({
 *   queryKey: ['data', httpRequestConfig.space],
 *   queryFn: () => fetchData({ httpRequestConfig }),
 *   enabled: isEnabled,
 * });
 * ```
 */
export function useQueryConfig(): QueryConfigResult {
  // Get space from store (single source of truth, initialized from server props)
  const storeSpaceId = useSpaceId();
  const isHydrated = useIsSpaceHydrated();
  // Track Firebase token availability so queries don't fire before the JWT
  // cookie has been written on first paint.
  const isTokenReady = useTokenReady();

  const config = React.useMemo(() => {
    // Always use store space - it's initialized from server props for SSR consistency
    const spaceId = storeSpaceId;
    const hasSpace = spaceId !== undefined && spaceId !== null && spaceId !== '';

    // Get target and auth token from cookies
    // These are available on client, empty values on server (but queries disabled anyway)
    const target = getTargetEnvironmentClient();
    const authToken = resolveAuthTokenSync();
    const hasAuth = authToken !== '' || isTokenReady === true;

    const httpRequestConfig: HttpServiceConfig = {
      space: spaceId,
      target,
      authToken,
    };

    return {
      httpRequestConfig,
      // Only enable queries after hydration to prevent SSR fetch attempts AND
      // after auth is ready — otherwise the first paint fires an
      // unauthenticated request that 401s, surfacing as "Something went wrong"
      // until a manual retry hits with the now-resolved cookie.
      // Query keys are correct regardless, so HydrationBoundary cache works.
      isEnabled: isHydrated === true && hasSpace === true && hasAuth === true,
      spaceId,
      hasSpace,
      isHydrated,
    };
  }, [storeSpaceId, isHydrated, isTokenReady]);

  return config;
}

/**
 * Hook to check if queries should be enabled
 * Shorthand for cases where you only need the enabled state
 */
export function useQueryEnabled(): boolean {
  const { isEnabled } = useQueryConfig();
  return isEnabled;
}

/**
 * Hook to get current space ID from query config
 * Use this when you need just the space ID without full config
 * @deprecated Use useSpaceId from @/stores/space instead
 */
export function useSpaceFromCookie(): string {
  const { spaceId } = useQueryConfig();
  return spaceId;
}
